import { useMemo } from 'react';
import { lessonEndInWarsaw, lessonStartInWarsaw } from 'core/utils/lessonTime';

import { useNow } from '../Meta/useNow';
import { useGroupLessons, IGroupLessonRow } from './useGroupTabsData';

/**
 * useGroupRecentActivity — a thin, side-effect-light selector that composes a
 * small "recent activity" feed for the Overview tab (Req 2.3) WITHOUT
 * introducing a new network query.
 *
 * Design note (honest limitation): the only group-scoped timeline signal that
 * is reliably already in the Apollo cache when the Overview tab is open is the
 * lesson schedule. The Overview tab's `UpcomingLessonsSection` already reads
 * `useGroupLessons(groupId)`; this hook re-reads the SAME query (Apollo dedupes
 * by operation + variables, so no extra round-trip is issued) and projects the
 * already-passed lessons into recent-activity items. Materials/sync timestamps
 * would each require triggering their own queries here — which would violate
 * the "no new query" constraint — so they are intentionally NOT pulled. If a
 * cheaper always-cached signal becomes available later, it can be folded into
 * the same projection.
 *
 * The hook degrades gracefully: when no lessons are available (loading, error,
 * or genuinely empty), `items` is `[]`, and `RecentActivitySection` shows its
 * localized "no recent activity" message.
 */

/** How many recent items to surface (most-recent first). */
const RECENT_ACTIVITY_LIMIT = 5;

/**
 * The kind of change a recent-activity item represents.
 *
 * Перепроверка G41: `lessonPassed` — урок закрыт преподавателем
 * (`finished`, то же поле, что считает «Уроков проведено» и `passedLessons`
 * сводки посещаемости). `lessonUnclosed` — время урока прошло, а закрыть
 * его не закрыли: «проведён» рядом с баннером «Прошедших занятий не закрыто»
 * противоречил ему на том же экране.
 */
export type GroupRecentActivityKind = 'lessonPassed' | 'lessonUnclosed';

/**
 * One recent-activity item. Intentionally i18n-free: the component localizes
 * using `kind` (e.g. `pages.groupDetail.overview.recentActivity.lessonPassed`)
 * and renders `title` (a plain subject name) verbatim.
 */
export interface IGroupRecentActivityItem {
  /** Stable identity for React keys (the underlying lesson id). */
  id: string;
  /** Discriminator the component maps to a localized label. */
  kind: GroupRecentActivityKind;
  /** Plain-text subject of the change (the lesson name). */
  title: string;
  /** Epoch millis the change occurred at; the feed is sorted descending. */
  timestamp: number;
}

interface IUseGroupRecentActivity {
  items: IGroupRecentActivityItem[];
  loading: boolean;
}

/** Resolve the scheduled instant (epoch millis) of a lesson, or `null`. */
const toScheduledMillis = (lesson: IGroupLessonRow): number | null =>
  lessonStartInWarsaw(lesson)?.toMillis() ?? null;

/**
 * When the lesson is over (epoch millis): its end, else its start when the
 * end is unknown. «Проведён» = закончился, а не начался: идущий урок иначе
 * стоял бы сразу и в «Ближайших» с «идёт сейчас», и здесь как проведённый
 * (повторная проверка глазами 07.10).
 */
const toOverMillis = (lesson: IGroupLessonRow): number | null =>
  lessonEndInWarsaw(lesson)?.toMillis() ?? toScheduledMillis(lesson);

/**
 * Compose the recent-activity feed for a group from its already-cached lesson
 * schedule. Returns the most recently passed lessons (ended at/before now)
 * in reverse-chronological order, capped at `RECENT_ACTIVITY_LIMIT`: closed
 * ones as `lessonPassed`, the rest as `lessonUnclosed`.
 */
export const useGroupRecentActivity = (
  groupId?: string,
): IUseGroupRecentActivity => {
  const { group, loading } = useGroupLessons(groupId);
  // Перепроверка G6: «сейчас» — тикер раз в минуту в зависимостях, а не
  // однократный `Date.now()`: урок, закончившийся при открытой карточке,
  // попадает в недавние без перезагрузки.
  const now = useNow();

  const items = useMemo<IGroupRecentActivityItem[]>(() => {
    const lessons = group?.lessons;
    if (!lessons || lessons.length === 0) {
      return [];
    }

    return lessons
      .map((lesson) => ({
        lesson,
        scheduled: toScheduledMillis(lesson),
        over: toOverMillis(lesson),
      }))
      .filter(
        (
          entry,
        ): entry is {
          lesson: IGroupLessonRow;
          scheduled: number;
          over: number;
        } =>
          entry.scheduled !== null && entry.over !== null && entry.over <= now,
      )
      .sort((a, b) => b.over - a.over)
      .slice(0, RECENT_ACTIVITY_LIMIT)
      .map(
        ({ lesson, over }): IGroupRecentActivityItem => ({
          id: lesson.id,
          kind: lesson.finished ? 'lessonPassed' : 'lessonUnclosed',
          title: lesson.name || '',
          // Событие — конец урока: «прошёл» он тогда, когда закончился.
          timestamp: over,
        }),
      );
  }, [group?.lessons, now]);

  return { items, loading };
};
